import React, { useState, useEffect } from 'react';
import { Star, X } from 'lucide-react';
import reviewService from '../../services/reviewService';
import { useAuth } from '../../context/AuthContext';
import { useFeedback } from '../../contexts/FeedbackContext';

const ReviewModal = ({
  isOpen,
  onClose,
  campaignId,
  participantId,
  revieweeId,
  revieweeType,
  revieweeName,
  reviewerType,
}) => {
  const { currentUser } = useAuth();
  const { showSuccess, showError } = useFeedback();
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [existingReview, setExistingReview] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen || !campaignId || !currentUser) return;

    const checkExisting = async () => {
      setLoading(true);
      try {
        const existing = await reviewService.getReviewByCampaignAndReviewer(
          campaignId,
          currentUser.uid,
          participantId
        );
        if (existing) {
          setExistingReview(existing);
          setRating(existing.rating || 0);
          setReviewText(existing.reviewText || '');
        } else {
          setExistingReview(null);
          setRating(0);
          setReviewText('');
        }
      } catch (err) {
        // Silently fail — user can still submit
      } finally {
        setLoading(false);
      }
    };

    checkExisting();
  }, [isOpen, campaignId, participantId, currentUser]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!currentUser) {
      showError('Authentication required.', 'Please log in to submit a review.', 4000);
      return;
    }
    if (rating < 1 || rating > 5) {
      showError('Rating required.', 'Please select a star rating (1-5).', 4000);
      return;
    }
    if (!reviewText.trim()) {
      showError('Review required.', 'Please write a brief review.', 4000);
      return;
    }

    setSubmitting(true);
    try {
      if (existingReview) {
        await reviewService.updateReview(existingReview.id, rating, reviewText.trim());
        showSuccess('Review updated.', 'Your review has been updated successfully.', 3000);
      } else {
        await reviewService.createReview(
          currentUser.uid,
          reviewerType,
          revieweeId,
          revieweeType,
          campaignId,
          participantId,
          rating,
          reviewText.trim()
        );
        showSuccess('Review submitted.', 'Thank you for your feedback!', 3000);
      }
      onClose();
    } catch (err) {
      const message = err.message?.includes('already exists')
        ? 'You have already reviewed this participant for this campaign.'
        : 'Failed to submit review. Please try again.';
      showError('Review failed.', message, 5000);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setRating(0);
    setHoverRating(0);
    setReviewText('');
    setExistingReview(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            {existingReview ? 'Edit Review' : 'Leave a Review'}
          </h3>
          <button type="button" onClick={handleClose} className="text-gray-400 hover:text-gray-500 transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        {loading ? (
          <div className="p-6 text-center text-sm text-gray-500">Loading...</div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500">Reviewing:</p>
              <p className="text-sm font-medium text-gray-900">{revieweeName || 'Unknown'}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Rating</label>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRating(star)}
                    onMouseEnter={() => setHoverRating(star)}
                    onMouseLeave={() => setHoverRating(0)}
                    className="p-1 transition-transform hover:scale-110"
                    aria-label={`${star} star${star > 1 ? 's' : ''}`}
                  >
                    <Star
                      className={`h-7 w-7 ${
                        star <= (hoverRating || rating)
                          ? 'fill-amber-400 text-amber-400'
                          : 'text-gray-300'
                      }`}
                    />
                  </button>
                ))}
                {rating > 0 && (
                  <span className="ml-2 text-sm text-gray-500">
                    {['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'][rating]}
                  </span>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="reviewText" className="block text-sm font-medium text-gray-700 mb-1">
                Review
              </label>
              <textarea
                id="reviewText"
                value={reviewText}
                onChange={(e) => setReviewText(e.target.value)}
                required
                rows={4}
                maxLength={500}
                className="block w-full border border-gray-300 rounded-md py-2.5 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition duration-150 ease-in-out"
                placeholder="Share your experience working with this person..."
              />
              <p className="mt-1 text-xs text-gray-400">{reviewText.length}/500 characters</p>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={handleClose} className="inline-flex justify-center py-2.5 px-6 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors">
                Cancel
              </button>
              <button type="submit" disabled={submitting} className="inline-flex justify-center py-2.5 px-6 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                {submitting ? 'Submitting...' : existingReview ? 'Update Review' : 'Submit Review'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ReviewModal;
